/**
 * CSAT Content Tab (inside IBDetailsPage).
 *
 * Three sub-tabs:
 *   1. Results     — dashboard with 4-category breakdown, trend, comments
 *   2. Triggers    — generate/manage survey links (unique per-client or general)
 *   3. Questions   — edit 40 default questions per channel (or use globals)
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardList, Link2, Sparkles, Copy, Trash2, Plus, RefreshCw, MessageSquare,
  Save, X, Edit3, Send, Star, GraduationCap, Package, Radio, Users, BarChart3, Languages, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/SectionHeader';
import { buildPublicSurveyUrl } from '@/config/surveyDomain';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['community', 'education', 'product', 'signal'];
const CAT_META = {
  community: { label: 'Community', Icon: Users,         color: '#F43F5E', gradient: 'from-rose-500 to-orange-500' },
  education: { label: 'Education', Icon: GraduationCap, color: '#3B82F6', gradient: 'from-blue-500 to-indigo-500' },
  product:   { label: 'Product',   Icon: Package,       color: '#10B981', gradient: 'from-emerald-500 to-teal-500' },
  signal:    { label: 'Signal',    Icon: Radio,         color: '#F59E0B', gradient: 'from-amber-500 to-yellow-500' },
};

function buildPublicUrl(token) {
  return buildPublicSurveyUrl(token);
}

function ScoreCard({ label, Icon, score, color, count }) {
  const pct = score || 0;
  const weighted = (pct * 0.25).toFixed(1);
  return (
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}22` }}>
          <Icon size={12} strokeWidth={2.5} style={{ color }} />
        </div>
        <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">{label}</p>
      </div>
      <div className="flex items-baseline gap-1.5 flex-wrap">
        <p className="text-3xl font-bold text-white tabular-nums leading-none">
          {pct.toFixed(1)}<span className="text-sm text-[#6B7280]">%</span>
        </p>
        <p className="text-[10px] text-[#9CA3AF] tabular-nums" title="Weighted contribution to total CSAT">
          (<span className="text-[#F97316] font-semibold">{weighted}%</span> of 25%)
        </p>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-[hsl(var(--surface-elevated))] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} />
      </div>
      <p className="text-[9px] text-[#6B7280] mt-1.5">Weight 25% · {count || 0} responses</p>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
        active
          ? 'bg-gradient-to-r from-[#F43F5E] to-[#F97316] text-white shadow-lg'
          : 'text-[#9CA3AF] hover:text-white hover:bg-[hsl(var(--surface-elevated))]'
      }`}
    >
      {children}
    </button>
  );
}

// --- Results sub-tab ---------------------------------------------------------
function ResultsPanel({ ibId }) {
  const monthOptions = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const short = d.toLocaleDateString('en-US', { month: 'short' });
      const year = d.getFullYear();
      arr.push({ value: val, short, year });
    }
    return arr.reverse();
  }, []);

  const [month, setMonth] = useState(monthOptions[monthOptions.length - 1].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/csat/results/${ibId}?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error('Gagal load results');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [ibId, month]);

  const period = data?.period_aggregate;
  const allTime = data?.all_time_aggregate;

  return (
    <div className="space-y-5">
      {/* Compact month dropdown */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Month</p>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#F97316]/50"
            data-testid="csat-month-select"
          >
            {monthOptions.slice().reverse().map(m => (
              <option key={m.value} value={m.value}>{m.short} {m.year}</option>
            ))}
          </select>
        </div>
        <Button onClick={load} disabled={loading} size="sm" variant="outline" data-testid="csat-refresh">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Overall score card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1 bg-gradient-to-br from-[#F43F5E]/15 to-[#F97316]/15 border border-[#F43F5E]/30 rounded-2xl p-5">
          <p className="text-[10px] text-[#F97316] uppercase tracking-wider mb-1">CSAT Score — {month}</p>
          <p className="text-5xl font-bold text-white tabular-nums leading-none">
            {(period?.total_score || 0).toFixed(1)}<span className="text-xl text-[#6B7280]">%</span>
          </p>
          <p className="text-xs text-[#9CA3AF] mt-2">{period?.count || 0} response(s) this month</p>
          <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-xs">
            <span className="text-[#6B7280]">All-time</span>
            <span className="text-white font-mono">{(allTime?.total_score || 0).toFixed(1)}% · {allTime?.count || 0}</span>
          </div>
        </div>
        <div className="md:col-span-2 grid grid-cols-2 gap-3">
          {CATEGORIES.map(c => (
            <ScoreCard
              key={c}
              Icon={CAT_META[c].Icon}
              label={CAT_META[c].label}
              color={CAT_META[c].color}
              score={period?.scores?.[c] || 0}
              count={period?.distribution?.[c] || 0}
            />
          ))}
        </div>
      </div>

      {/* Monthly trend */}
      {(data?.trend?.length || 0) > 1 && (
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
          <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-3">12-Month Trend</p>
          <div className="flex items-end gap-2 h-24">
            {data.trend.map(t => {
              const h = Math.max(4, (t.total_score / 100) * 92);
              return (
                <div key={t.month} className="flex-1 flex flex-col items-center gap-1 group" data-testid={`trend-${t.month}`}>
                  <span className="text-[9px] text-[#6B7280] font-mono opacity-0 group-hover:opacity-100">{t.total_score}%</span>
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-[#F43F5E] to-[#F97316] transition-all hover:opacity-80"
                    style={{ height: `${h}px` }}
                    title={`${t.month}: ${t.total_score}% (${t.count})`}
                  />
                  <span className="text-[9px] text-[#6B7280]">{t.month.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent responses */}
      {(data?.responses?.length || 0) > 0 && (
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users size={14} className="text-[#9CA3AF]" />
            <p className="text-sm text-white font-semibold">Recent Responses</p>
            <span className="text-xs text-[#6B7280]">({data.responses.length})</span>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {data.responses.map((r) => (
              <div key={r.id} className="bg-[hsl(var(--surface-elevated))]/40 rounded-lg p-3 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-semibold">{r.client_name || 'Anonymous'}</span>
                  <span className="text-[#F97316] font-bold text-sm tabular-nums">{(r.total_score || 0).toFixed(1)}%</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-[#9CA3AF]">
                  {CATEGORIES.map(c => {
                    const RIcon = CAT_META[c].Icon;
                    return (
                      <span key={c} className="inline-flex items-center gap-1">
                        <RIcon size={10} strokeWidth={2.25} style={{ color: CAT_META[c].color }} />
                        {r.scores?.[c] !== null && r.scores?.[c] !== undefined ? `${r.scores[c]}%` : '—'}
                      </span>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#6B7280] mt-1">
                  {r.submitted_at?.slice(0, 16).replace('T', ' ')} · {r.mode} · {r.client_email || r.client_phone || 'no contact'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent comments */}
      {(data?.recent_comments?.length || 0) > 0 && (
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare size={14} className="text-[#9CA3AF]" />
            <p className="text-sm text-white font-semibold">Recent Comments & Suggestions</p>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {data.recent_comments.map((c, i) => {
              const meta = CAT_META[c.category] || {};
              const CIcon = meta.Icon;
              return (
                <div key={i} className="bg-[hsl(var(--surface-elevated))]/40 rounded-lg p-3 text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${meta.color}20`, color: meta.color }}>
                      {CIcon && <CIcon size={10} strokeWidth={2.5} />} {meta.label}
                    </span>
                    {c.rating && (
                      <span className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }, (_, i) => (
                          <Star key={i} size={10} className={i < c.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-600'} />
                        ))}
                      </span>
                    )}
                    <span className="text-[10px] text-[#6B7280] ml-auto">{c.client_name}</span>
                  </div>
                  <p className="text-white/90 leading-relaxed">&ldquo;{c.comment}&rdquo;</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(period?.count || 0) === 0 && !loading && (
        <div className="text-center py-12 bg-[hsl(var(--surface))] border border-dashed border-[hsl(var(--border))] rounded-xl">
          <ClipboardList size={32} className="text-[#6B7280] mx-auto mb-2" />
          <p className="text-sm text-white font-semibold">Belum ada response untuk {month}</p>
          <p className="text-xs text-[#9CA3AF] mt-1">Trigger survey link dari tab &ldquo;Triggers&rdquo; untuk mula collect feedback.</p>
        </div>
      )}
    </div>
  );
}

// --- Triggers sub-tab --------------------------------------------------------
function TriggersPanel({ ib }) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [mode, setMode] = useState('general');
  const [expiresDays, setExpiresDays] = useState(30);
  const [selectedClient, setSelectedClient] = useState('');
  const [clients, setClients] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/csat/surveys/${ib.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSurveys(r.data.surveys || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/clients/${ib.id}?status=active`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(r.data.clients || []);
    } catch (e) {
      setClients([]);
    }
  };

  useEffect(() => { load(); loadClients(); }, [ib.id]);

  const trigger = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        ib_id: ib.id,
        mode,
        expires_days: parseInt(expiresDays) || 30,
      };
      if (mode === 'unique' && selectedClient) body.client_id = selectedClient;
      const r = await axios.post(`${API}/csat/trigger`, body, { headers: { Authorization: `Bearer ${token}` } });
      const url = buildPublicUrl(r.data.survey.token);
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Survey link created & copied to clipboard!');
      } catch {
        toast.success('Survey link created!');
      }
      setSelectedClient('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal create survey');
    } finally {
      setCreating(false);
    }
  };

  const removeSurvey = async (id) => {
    if (!window.confirm('Delete this survey link? Existing responses will also be deleted.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/csat/surveys/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const copyLink = async (t) => {
    try {
      await navigator.clipboard.writeText(buildPublicUrl(t));
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="space-y-5">
      {/* New survey */}
      <div className="bg-gradient-to-br from-[#F43F5E]/10 to-[#F97316]/10 border border-[#F43F5E]/30 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="text-[#F97316]" size={16} />
          <p className="text-sm font-semibold text-white">Trigger New CSAT Survey</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1 block">Mode</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm text-white"
              data-testid="trigger-mode"
            >
              <option value="general">General (channel-wide, reusable)</option>
              <option value="unique">Unique (one-shot per client)</option>
            </select>
          </div>
          {mode === 'unique' && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1 block">Client (optional)</label>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm text-white"
                data-testid="trigger-client"
              >
                <option value="">— Anonymous (fill in form) —</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name} · {c.product}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1 block">Expires in (days)</label>
            <input
              type="number"
              min="1"
              max="365"
              value={expiresDays}
              onChange={(e) => setExpiresDays(e.target.value)}
              className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm text-white"
              data-testid="trigger-expires"
            />
          </div>
        </div>
        <Button
          onClick={trigger}
          disabled={creating}
          className="mt-4 bg-gradient-to-r from-[#F43F5E] to-[#F97316] hover:opacity-90 text-white"
          data-testid="trigger-btn"
        >
          {creating ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
          Generate Link
        </Button>
      </div>

      {/* Active surveys */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Link2 size={14} className="text-[#9CA3AF]" />
          <p className="text-sm font-semibold text-white">Survey Links ({surveys.length})</p>
        </div>
        {surveys.length === 0 ? (
          <p className="text-xs text-[#6B7280] py-6 text-center">Belum ada survey link. Generate satu di atas ↑</p>
        ) : (
          <div className="space-y-2">
            {surveys.map(s => {
              const url = buildPublicUrl(s.token);
              const isCompleted = s.status === 'completed';
              const isExpired = s.expires_at && s.expires_at < new Date().toISOString();
              return (
                <div key={s.id} className="bg-[hsl(var(--surface-elevated))]/40 rounded-lg p-3 flex items-center gap-3" data-testid={`survey-row-${s.id}`}>
                  <div className={`w-2 h-2 rounded-full ${isExpired ? 'bg-red-500' : isCompleted ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-white">{s.mode === 'unique' ? 'Unique' : 'General'}</span>
                      {s.client_name && <span className="text-xs text-[#9CA3AF]">{s.client_name}</span>}
                      <span className="text-[10px] text-[#6B7280]">· {s.response_count || 0} response(s)</span>
                      {isExpired && <span className="text-[10px] text-red-400">EXPIRED</span>}
                      {isCompleted && <span className="text-[10px] text-emerald-400">COMPLETED</span>}
                    </div>
                    <p className="text-[10px] text-[#6B7280] font-mono truncate mt-0.5">{url}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => copyLink(s.token)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-elevated))] text-[#9CA3AF] hover:text-white" title="Copy" data-testid={`copy-${s.id}`}>
                      <Copy size={13} />
                    </button>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-elevated))] text-[#9CA3AF] hover:text-white" title="Preview">
                      <Link2 size={13} />
                    </a>
                    <button onClick={() => removeSurvey(s.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-[#9CA3AF] hover:text-red-400" title="Delete" data-testid={`delete-${s.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Questions sub-tab -------------------------------------------------------
function QuestionsPanel({ ib }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [switchingLang, setSwitchingLang] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/csat/questions?ib_id=${ib.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (e) {
      toast.error('Gagal load questions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [ib.id]);

  const usingChannelOverride = useMemo(() => {
    if (!data?.questions) return false;
    return data.questions.some(q => q.ib_id === ib.id);
  }, [data, ib.id]);

  const currentLang = data?.language || 'ms';

  const copyToChannel = async (lang) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/csat/questions/copy-defaults/${ib.id}?lang=${lang}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Copied ${lang === 'en' ? 'English' : 'Malay'} defaults to this channel.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal copy');
    }
  };

  const switchLanguage = async (newLang) => {
    if (newLang === currentLang) return;
    // Warn if channel already has responses — reset destroys historical mappings
    let responseCount = 0;
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/csat/scorecard/${ib.id}`, { headers: { Authorization: `Bearer ${token}` } });
      responseCount = r.data?.response_count || 0;
    } catch (e) { /* ignore */ }

    let confirmMsg = `Switch this channel to ${newLang === 'en' ? 'English' : 'Bahasa Melayu'}?\n\nAll ${usingChannelOverride ? 'customised questions' : 'default questions'} for this channel will be replaced with the ${newLang === 'en' ? 'English' : 'Malay'} defaults.`;
    if (responseCount > 0) {
      confirmMsg += `\n\nWARNING: This channel already has ${responseCount} response(s). Switching language creates new question IDs, so the Full Report drilldown will show historical distribution as 0 (aggregate scores stay correct). Recommended: keep the current language for continuity.`;
    }
    if (!window.confirm(confirmMsg)) return;
    setSwitchingLang(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/csat/settings/${ib.id}`, { language: newLang, reset_questions: true }, { headers: { Authorization: `Bearer ${token}` } });
      // Re-seed the new language bank
      await axios.post(`${API}/csat/questions/copy-defaults/${ib.id}?lang=${newLang}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Switched to ${newLang === 'en' ? 'English' : 'Bahasa Melayu'}. Question bank refreshed.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to switch language');
    } finally {
      setSwitchingLang(false);
    }
  };

  const saveEdit = async (qid) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/csat/questions/${qid}`, { question_text: editText }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Saved');
      setEditingId(null);
      load();
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const removeQuestion = async (qid) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/csat/questions/${qid}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const addQuestion = async (category) => {
    const text = window.prompt(`Add new ${category} question:`);
    if (!text) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/csat/questions`, {
        ib_id: usingChannelOverride ? ib.id : null,
        category,
        question_text: text.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Added');
      load();
    } catch (e) {
      toast.error('Failed to add');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm font-semibold text-white flex items-center gap-2">
            <ClipboardList size={14} className="text-[#F97316]" />
            {usingChannelOverride ? 'Channel-specific questions' : 'Using global defaults'}
          </p>
          <p className="text-xs text-[#9CA3AF] mt-1">
            {usingChannelOverride
              ? `Edits below only affect ${ib.name}. Total: ${data?.questions?.length || 0} questions.`
              : 'Edits will affect ALL channels. Click "Copy to channel" first to customize for this IB only.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Language switcher */}
          <div className="flex items-center gap-1 bg-[hsl(var(--surface-elevated))]/40 rounded-lg p-1" data-testid="csat-lang-switch">
            <Languages size={12} className="text-[#9CA3AF] ml-1" />
            <button
              onClick={() => switchLanguage('ms')}
              disabled={switchingLang}
              className={`px-2.5 py-1 text-[10px] font-medium rounded ${currentLang === 'ms' ? 'bg-[#F97316] text-white' : 'text-[#9CA3AF] hover:text-white'}`}
              data-testid="csat-lang-ms"
            >
              BM
            </button>
            <button
              onClick={() => switchLanguage('en')}
              disabled={switchingLang}
              className={`px-2.5 py-1 text-[10px] font-medium rounded ${currentLang === 'en' ? 'bg-[#F97316] text-white' : 'text-[#9CA3AF] hover:text-white'}`}
              data-testid="csat-lang-en"
            >
              EN
            </button>
          </div>
          {!usingChannelOverride && (
            <Button onClick={() => copyToChannel(currentLang)} size="sm" className="bg-[#F97316]/20 hover:bg-[#F97316]/30 text-[#F97316] border border-[#F97316]/40" data-testid="copy-defaults-btn">
              <Copy size={13} className="mr-1.5" /> Copy defaults to this channel
            </Button>
          )}
        </div>
      </div>

      {CATEGORIES.map(cat => {
        const CatIcon = CAT_META[cat].Icon;
        return (
        <div key={cat} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-white flex items-center gap-2">
              <span className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${CAT_META[cat].color}22` }}>
                <CatIcon size={12} strokeWidth={2.5} style={{ color: CAT_META[cat].color }} />
              </span>
              {data?.labels?.[cat] || CAT_META[cat].label} (25%)
            </p>
            <button
              onClick={() => addQuestion(cat)}
              className="text-[10px] text-[#F97316] hover:text-[#F97316]/80 flex items-center gap-1"
              data-testid={`add-question-${cat}`}
            >
              <Plus size={12} /> Add question
            </button>
          </div>
          <div className="space-y-1.5">
            {(data?.grouped?.[cat] || []).map((q, idx) => (
              <div key={q.id} className="bg-[hsl(var(--surface-elevated))]/40 rounded-lg p-2.5 flex items-start gap-2" data-testid={`question-row-${q.id}`}>
                <span className="text-xs text-[#6B7280] mt-1 flex-shrink-0 font-mono w-5">{idx + 1}.</span>
                {editingId === q.id ? (
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="flex-1 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md px-2 py-1 text-xs text-white"
                      autoFocus
                    />
                    <button onClick={() => saveEdit(q.id)} className="p-1 text-emerald-400 hover:bg-emerald-400/10 rounded" data-testid={`save-${q.id}`}>
                      <Save size={13} />
                    </button>
                    <button onClick={() => setEditingId(null)} className="p-1 text-[#9CA3AF] hover:bg-red-500/10 rounded">
                      <X size={13} />
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="flex-1 text-xs text-white/90 leading-relaxed">
                      {q.question_text}
                      {q.question_type === 'open_text' && (
                        <span className="ml-2 text-[9px] text-[#F97316] uppercase tracking-wider">Open text</span>
                      )}
                    </p>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => { setEditingId(q.id); setEditText(q.question_text); }} className="p-1 text-[#9CA3AF] hover:text-white rounded" data-testid={`edit-${q.id}`}>
                        <Edit3 size={12} />
                      </button>
                      <button onClick={() => removeQuestion(q.id)} className="p-1 text-[#9CA3AF] hover:text-red-400 rounded" data-testid={`del-${q.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      );})}

      {loading && (
        <div className="text-center py-4">
          <RefreshCw size={16} className="animate-spin mx-auto text-[#6B7280]" />
        </div>
      )}
    </div>
  );
}

// --- Report sub-tab ----------------------------------------------------------
// Deep per-question drilldown: rating distribution, avg, and comments per Q.
function ReportPanel({ ib }) {
  // Build last-12-months list, ending on current month
  const monthOptions = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const short = d.toLocaleDateString('en-US', { month: 'short' });
      const year = d.getFullYear();
      arr.push({ value: val, short, year, label: `${short} ${year}` });
    }
    return arr.reverse();
  }, []);

  const [month, setMonth] = useState(monthOptions[monthOptions.length - 1].value);
  const [allTime, setAllTime] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openCategory, setOpenCategory] = useState('community');

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = allTime ? `${API}/csat/report/${ib.id}` : `${API}/csat/report/${ib.id}?month=${month}`;
      const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [ib.id, month, allTime]);

  const totalResponses = data?.response_count || 0;

  return (
    <div className="space-y-5">
      {/* Compact month dropdown + all-time toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Period</p>
          <select
            value={allTime ? '__all' : month}
            onChange={(e) => {
              if (e.target.value === '__all') {
                setAllTime(true);
              } else {
                setAllTime(false);
                setMonth(e.target.value);
              }
            }}
            className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#F97316]/50"
            data-testid="report-month-select"
          >
            <option value="__all">All-time</option>
            {monthOptions.slice().reverse().map(m => (
              <option key={m.value} value={m.value}>{m.short} {m.year}</option>
            ))}
          </select>
          <span className="text-[10px] text-[#6B7280] pl-2 border-l border-[hsl(var(--border))]">
            <span className="font-semibold text-white tabular-nums">{totalResponses}</span> response(s)
          </span>
        </div>
        <Button onClick={load} disabled={loading} size="sm" variant="outline">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Category summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {CATEGORIES.map(cat => {
          const s = data?.summary?.[cat];
          const CatIcon = CAT_META[cat].Icon;
          const isOpen = openCategory === cat;
          const pct = s?.avg_score_pct || 0;
          const weighted = (pct * 0.25).toFixed(1);
          return (
            <button
              key={cat}
              onClick={() => setOpenCategory(cat)}
              className={`text-left p-4 rounded-xl border transition-all ${
                isOpen
                  ? 'border-[#F97316]/50 bg-[hsl(var(--surface))] shadow-lg'
                  : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))]/60 hover:bg-[hsl(var(--surface))]'
              }`}
              data-testid={`report-cat-${cat}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${CAT_META[cat].color}22` }}>
                  <CatIcon size={12} strokeWidth={2.5} style={{ color: CAT_META[cat].color }} />
                </div>
                <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">{s?.label || CAT_META[cat].label}</p>
              </div>
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <p className="text-2xl font-bold text-white tabular-nums leading-none">
                  {pct.toFixed(1)}<span className="text-xs text-[#6B7280]">%</span>
                </p>
                <p className="text-[10px] text-[#9CA3AF] tabular-nums">
                  (<span className="text-[#F97316] font-semibold">{weighted}%</span> of 25%)
                </p>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-[hsl(var(--surface-elevated))] overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: CAT_META[cat].color }} />
              </div>
              <p className="text-[10px] text-[#6B7280] mt-1.5">
                {s?.answered_question_count || 0}/{s?.question_count || 0} Qs · {s?.total_comments || 0} comments
              </p>
            </button>
          );
        })}
      </div>

      {/* Per-question drilldown for selected category */}
      {openCategory && (
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            {(() => { const IC = CAT_META[openCategory].Icon; return (
              <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${CAT_META[openCategory].color}22` }}>
                <IC size={14} strokeWidth={2.5} style={{ color: CAT_META[openCategory].color }} />
              </div>
            ); })()}
            <p className="text-base font-semibold text-white">
              {data?.summary?.[openCategory]?.label || CAT_META[openCategory].label} — Per-Question Report
            </p>
          </div>
          <div className="space-y-3">
            {(data?.per_category?.[openCategory] || []).map((q, idx) => (
              <QuestionReportRow key={q.question_id} q={q} idx={idx} color={CAT_META[openCategory].color} totalResponses={totalResponses} />
            ))}
          </div>
        </div>
      )}

      {totalResponses === 0 && !loading && (
        <div className="text-center py-12 bg-[hsl(var(--surface))] border border-dashed border-[hsl(var(--border))] rounded-xl">
          <BarChart3 size={32} className="text-[#6B7280] mx-auto mb-2" />
          <p className="text-sm text-white font-semibold">No responses for {allTime ? 'all-time' : month}</p>
          <p className="text-xs text-[#9CA3AF] mt-1">Once clients start answering surveys, the per-question breakdown will show here.</p>
        </div>
      )}
    </div>
  );
}

function QuestionReportRow({ q, idx, color, totalResponses }) {
  const [showComments, setShowComments] = useState(false);
  const dist = q.distribution || {};
  const maxDist = Math.max(1, ...Object.values(dist).map(v => Number(v) || 0));
  const isOpenText = q.question_type === 'open_text';

  return (
    <div className="bg-[hsl(var(--surface-elevated))]/40 rounded-lg p-3" data-testid={`report-q-${q.question_id}`}>
      <div className="flex items-start gap-3 mb-2">
        <span className="text-xs text-[#6B7280] mt-0.5 font-mono flex-shrink-0 w-5">{idx + 1}.</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white/90 leading-snug">{q.question_text}</p>
          {isOpenText && (
            <span className="inline-block mt-1 text-[9px] text-[#F97316] uppercase tracking-wider">Open text</span>
          )}
        </div>
        {!isOpenText && (
          <div className="text-right flex-shrink-0">
            <p className="text-lg font-bold text-white tabular-nums leading-none">
              {(q.avg_rating || 0).toFixed(1)}<span className="text-xs text-[#6B7280]">/5</span>
            </p>
            <p className="text-[9px] text-[#6B7280]">{q.total_responses || 0} vote(s)</p>
          </div>
        )}
      </div>

      {/* Rating distribution bars (only for rating questions) */}
      {!isOpenText && (
        <div className="mt-3 ml-8 grid grid-cols-5 gap-1.5">
          {[1, 2, 3, 4, 5].map(r => {
            const count = Number(dist[String(r)] || 0);
            const barHeight = Math.max(4, (count / maxDist) * 32);
            const pct = q.total_responses ? Math.round((count / q.total_responses) * 100) : 0;
            return (
              <div key={r} className="flex flex-col items-center gap-1 group">
                <div className="text-[9px] text-[#6B7280] font-mono">{count > 0 ? count : ''}</div>
                <div className="w-full flex justify-center items-end h-8">
                  <div
                    className="w-full rounded-t transition-all group-hover:opacity-80"
                    style={{ height: `${barHeight}px`, backgroundColor: count > 0 ? color : '#3F3F46' }}
                    title={`${count} vote(s) · ${pct}%`}
                  />
                </div>
                <div className="flex items-center gap-0.5">
                  <Star size={9} className="fill-amber-400 text-amber-400" />
                  <span className="text-[9px] text-[#9CA3AF] font-mono">{r}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Comments toggle */}
      {(q.comments || []).length > 0 && (
        <>
          <button
            onClick={() => setShowComments(!showComments)}
            className="mt-3 ml-8 text-[10px] text-[#F97316] hover:text-[#F97316]/80 flex items-center gap-1"
            data-testid={`toggle-comments-${q.question_id}`}
          >
            {showComments ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {q.comments.length} comment{q.comments.length === 1 ? '' : 's'}
          </button>
          {showComments && (
            <div className="mt-2 ml-8 space-y-1.5">
              {q.comments.map((c, i) => (
                <div key={i} className="bg-black/30 rounded-md p-2 text-[11px]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white/80 font-semibold">{c.client_name}</span>
                    {c.rating && (
                      <span className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }, (_, k) => (
                          <Star key={k} size={8} className={k < c.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-700'} />
                        ))}
                      </span>
                    )}
                    <span className="ml-auto text-[9px] text-[#6B7280] font-mono">{c.submitted_at?.slice(0, 10)}</span>
                  </div>
                  <p className="text-white/90 leading-relaxed">&ldquo;{c.comment}&rdquo;</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- Main --------------------------------------------------------------------
export default function CSATContent({ ib }) {
  const [tab, setTab] = useState('results');
  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto w-full" data-testid="csat-content">
      <SectionHeader
        icon={ClipboardList}
        title="Customer Satisfaction (CSAT)"
        subtitle={`4-part survey · Community · Education · Product · Signal · for ${ib?.name}`}
        iconBg="bg-gradient-to-br from-[#F43F5E] to-[#F97316] shadow-[#F43F5E]/20"
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <TabButton active={tab === 'results'} onClick={() => setTab('results')} testid="csat-tab-results">Results</TabButton>
            <TabButton active={tab === 'report'} onClick={() => setTab('report')} testid="csat-tab-report">Full Report</TabButton>
            <TabButton active={tab === 'triggers'} onClick={() => setTab('triggers')} testid="csat-tab-triggers">Triggers</TabButton>
            <TabButton active={tab === 'questions'} onClick={() => setTab('questions')} testid="csat-tab-questions">Questions</TabButton>
          </div>
        }
      />
      {tab === 'results' && <ResultsPanel ibId={ib.id} />}
      {tab === 'report' && <ReportPanel ib={ib} />}
      {tab === 'triggers' && <TriggersPanel ib={ib} />}
      {tab === 'questions' && <QuestionsPanel ib={ib} />}
    </div>
  );
}
